import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, timer } from 'rxjs';
import { map, retry, delayWhen, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { BaseHttpServiceService } from 'src/app/sharedFeatures/services/base-http-service.service';
import { CurrentUserService } from 'src/app/sharedFeatures/services/current-user.service';
import {
  ActionPlanMaster,
  ActionPlanFilter,
  ActionStatusCount,
  HierarchyItem,
  DAdminRegionsResponse,
  ExportActionPlanResponse,
} from '../models/action-plan.model';
import {
  AiRecommendation,
  AiRecommendationFilter,
  AiRecommendationPagedFilter,
  AiRecommendationPagedResult,
} from '../models/ai-recommendation.model';
import { GenericResultModel } from 'src/app/sharedFeatures/models/generic-result.model';
import { ScheduleItem } from '../models/schedule-missing-action-plan/schedule-item.model';
import { ScheduleFilter } from '../models/schedule-missing-action-plan/schedule-filter.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ActionPlanService extends BaseHttpServiceService {
  private detailCodeController: string = `${this.mysteryShopperUrl}/DetailCode`;
  private actionPlanController: string = `${this.mysteryShopperUrl}/ChecklistActionPlan`;
  private actionPlanMasterController: string = `${this.mysteryShopperUrl.replace('/api', '/Api')}/ActionPlanMasterAPI`;
  private actionPlanDAdminController: string = `${this.mysteryShopperUrl}/ActionPlanDAdminAPI`;
  private actionPlanDetailsController: string = `${this.mysteryShopperUrl}/ActionPlanDetailsAPI`;
  private scheduleController: string = `${this.mysteryShopperUrl}/ScheduleApi`;
  private userController: string = `${this.mysteryShopperUrl}/User`;
  private actionPlanItemsAPIController: string = `${this.mysteryShopperUrl}/ActionPlanItemsAPI`;
  private actionItemCommentsAPIController: string = `${this.mysteryShopperUrl}/ActionItemCommentsAPI`;
  private actionItemMembersAPIController: string = `${this.mysteryShopperUrl}/ActionItemMembersAPI`;
  private configurationController: string = `${this.mysteryShopperUrl.replace('/api', '/Api')}/Configuration`;

  constructor(
    http: HttpClient,
    translateService: TranslateService,
    currentUserService: CurrentUserService
  ) {
    super(http, translateService, currentUserService);
  }

  // Get Action Plan Master List View
  getActionPlanMasterListView(
    filter: ActionPlanFilter
  ): Observable<ActionPlanMaster[]> {
    let url: string = `${this.actionPlanMasterController}/GetActionPlanMasterListView`;
    return this.postData<ActionPlanMaster[]>(url, filter);
  }

  // Get Action Plan Items Status Count
  getActionPlanItemsActionStatusCount(
    params: any
  ): Observable<ActionStatusCount[]> {
    const queryParams = new URLSearchParams();
    if (params.userId) queryParams.append('UserId', params.userId);
    if (params.createdOnFrom)
      queryParams.append('DateFrom', params.createdOnFrom);
    if (params.createdOnTo) queryParams.append('DateTo', params.createdOnTo);
    if (params.categoryId) queryParams.append('CategoryId', params.categoryId);
    queryParams.append(
      'getOnlyActionPlans',
      params.isOverdue?.toString() || 'false'
    );
    if (params.itemId) queryParams.append('ItemId', params.itemId);
    queryParams.append('PageNumber', params.pageNumber?.toString() || '1');
    queryParams.append('PageSize', params.pageSize?.toString() || '10');
    if (params.apmStatusId)
      queryParams.append('APStatusId', params.apmStatusId);
    if (params.filterStatus)
      queryParams.append('APActionStatus', params.filterStatus);
    if (params.categoryParentId)
      queryParams.append('CategoryParentId', params.categoryParentId);
    queryParams.append(
      'IsSubmissionDate',
      params.isSubmissionDate?.toString() || 'false'
    );

    let url: string = `${this.actionPlanController}/GetActionPlanItemsActionStatusCount?${queryParams.toString()}`;
    return this.getData<ActionStatusCount[]>(url);
  }

  // Get Master Code by Multiple Codes
  getMasterCodeByMultiCode(code: string): Observable<HierarchyItem[]> {
    let url: string = `${this.detailCodeController}/GetMasterCodeByMultiCode?masterCode=${encodeURIComponent(code)}&UserId=&GetPadminAssignedDetails=`;
    return this.getData<any[]>(url).pipe(
      map((items: any[]) =>
        items.map((item: any) => ({
          ...item,
          // Map PascalCase to camelCase for consistency with frontend conventions
          hasCategoryRelationship:
            item.HasCategoryRelationship !== undefined
              ? item.HasCategoryRelationship
              : item.hasCategoryRelationship,
        }))
      )
    );
  }

  // Get Detail Codes by Parent Code
  getDetailCodesByParentCode(
    code: string,
    masterId: string
  ): Observable<HierarchyItem[]> {
    let url: string = `${this.detailCodeController}/GetDetailCodesByParentCode?code=${encodeURIComponent(code)}&MasterId=${masterId}&UserId=&GetPadminAssignedDetails=`;
    return this.getData<any[]>(url).pipe(
      map((items: any[]) => {
        const mapped = items.map((item: any) => {
          // Map PascalCase to camelCase for consistency with frontend conventions
          const hasCategoryRelationship =
            item.HasCategoryRelationship !== undefined
              ? item.HasCategoryRelationship
              : item.hasCategoryRelationship;
          return {
            ...item,
            hasCategoryRelationship: hasCategoryRelationship,
          };
        });
        return mapped;
      })
    );
  }

  // Master Code to Master Code ID mapping for Configuration API
  private masterCodeIdMapping: { [key: string]: string } = {
    Region: '82ACAD35-AC63-49BA-9072-1737228078A6',
    Division: '80739B5B-CB6F-4DEE-8ED4-E4F78901E4B5',
    Asset: '82313969-92AA-4550-95C5-73FD201C958F',
    Building: 'E46AAA71-EB24-43F4-B71F-63ABC472A6C4',
    SubBuilding: 'B6D31581-8E16-429E-8592-2CB92E206281',
    Location: 'B6D31581-8E16-429E-8592-2CB92E206281', // This appears to be a special case
  };

  // Get Department Admin Assigned Detail Code - Uses Configuration API
  getDadminAssignedDetailCode(
    userId: string,
    masterCode: string
  ): Observable<HierarchyItem[]> {
    const masterCodeId = this.masterCodeIdMapping[masterCode] || masterCode;
    let url: string = `${this.configurationController}/GetDadminAssignedDetailCode?userid=${userId}&&masterCodeId=${masterCodeId}`;
    return this.getData<DAdminRegionsResponse>(url).pipe(
      map(response => response.data || []),
      map((items: any[]) =>
        items.map((item: any) => ({
          ...item,
          hasCategoryRelationship:
            item.HasCategoryRelationship || item.hasCategoryRelationship,
        }))
      )
    );
  }

  // Get Schedule Category
  getScheduleCategory(filter: ActionPlanFilter): Observable<HierarchyItem[]> {
    let url: string = `${this.scheduleController}/ScheduleGetCategory`;
    return this.postData<HierarchyItem[]>(url, filter);
  }

  // Get detail code hierarchy
  getDetailCodeHierarchy(
    isCommunication: boolean,
    masterCodeId: string,
    parentId: string | null
  ): Observable<any> {
    let url: string = `${this.scheduleController}/GetDetailCodeHirearchy?isCommunication=${isCommunication}&masterCodeId=${encodeURIComponent(masterCodeId)}`;
    if (parentId) {
      url += `&parentId=${encodeURIComponent(parentId)}`;
    }
    return this.getData<any>(url);
  }

  // Get Schedule Category For Sub Building
  getScheduleCategoryForSubBuilding(payload: any): Observable<HierarchyItem[]> {
    let url: string = `${this.scheduleController}/GetScheduleCategoryForSubBuilding`;
    return this.postData<HierarchyItem[]>(url, payload);
  }

  // Send Action Plan Reminder
  sendActionPlanReminder(actionPlanMasterId: string): Observable<any> {
    let url: string = `${this.actionPlanMasterController}/SendActionPlanReminder?APMasterId=${actionPlanMasterId}`;
    return this.getData<any>(url);
  }

  // Get APM Status List
  getAPMStatusList(): Observable<HierarchyItem[]> {
    let url: string = `${this.detailCodeController}/GetMasterCodeByMultiCode?masterCode=${encodeURIComponent('APM Status')}&UserId=&GetPadminAssignedDetails=`;
    return this.getData<HierarchyItem[]>(url);
  }

  // Schedule-related API methods

  // Get Schedule List with Missing Action Plans
  getSchedules(filter: ScheduleFilter): Observable<any> {
    let url: string = `${this.scheduleController}/GetSchedulesWithMissingActionPlanPaging`;
    return this.postData<any>(url, filter);
  }

  // Get Schedule Action Plan Stats (for counts)
  getScheduleCounts(filter: ScheduleFilter): Observable<any> {
    let url: string = `${this.scheduleController}/GetSchedulesActionPlanStat`;
    return this.postData<any>(url, filter);
  }

  // Skip Schedule
  skipSchedule(scheduleId: string): Observable<any> {
    let url: string = `${this.scheduleController}/SkipSchedule?id=${scheduleId}`;
    return this.getData<any>(url);
  }

  // Unskip Schedule
  unskipSchedule(scheduleId: string): Observable<any> {
    let url: string = `${this.scheduleController}/UnskipSchedule?id=${scheduleId}`;
    return this.getData<any>(url);
  }

  // Get Regions (loaded by default) - Uses Division master code to get regions
  getRegions(): Observable<HierarchyItem[]> {
    return this.getMasterCodeByMultiCode('Division');
  }

  // Get DAdmin Assigned Regions - Uses specific Configuration API endpoint
  getDadminAssignedRegions(userId: string): Observable<HierarchyItem[]> {
    const masterCodeId = '82ACAD35-AC63-49BA-9072-1737228078A6';
    let url: string = `${this.configurationController}/GetDadminAssignedDetailCode?userid=${userId}&&masterCodeId=${masterCodeId}`;
    return this.getData<DAdminRegionsResponse>(url).pipe(
      map(response => response.data || [])
    );
  }

  // Get Divisions by Region
  getDivisionsByRegion(regionCode: string): Observable<HierarchyItem[]> {
    const divisionMasterId = '80739B5B-CB6F-4DEE-8ED4-E4F78901E4B5';
    return this.getDetailCodesByParentCode(regionCode, divisionMasterId);
  }

  // Get Assets by Division
  getAssetsByDivision(divisionCode: string): Observable<HierarchyItem[]> {
    const assetMasterId = '82313969-92AA-4550-95C5-73FD201C958F';
    return this.getDetailCodesByParentCode(divisionCode, assetMasterId);
  }

  // Get Buildings (Entities) by Asset
  getBuildingsByAsset(assetCode: string): Observable<HierarchyItem[]> {
    const buildingMasterId = 'E46AAA71-EB24-43F4-B71F-63ABC472A6C4';
    return this.getDetailCodesByParentCode(assetCode, buildingMasterId);
  }

  // Get Sub Buildings (Sub Entities) by Building
  getSubBuildingsByBuilding(buildingCode: string): Observable<HierarchyItem[]> {
    const subBuildingMasterId = 'B6D31581-8E16-429E-8592-2CB92E206281';
    return this.getDetailCodesByParentCode(buildingCode, subBuildingMasterId);
  }

  // Get Level 4 Options (SubBuild)
  getLevel4Options(): Observable<HierarchyItem[]> {
    return this.getMasterCodeByMultiCode('SubBuild');
  }

  // Get Level 5 Options (Location)
  getLevel5Options(): Observable<HierarchyItem[]> {
    return this.getMasterCodeByMultiCode('Location');
  }

  // Get Locations by Sub Building (for hierarchy)
  getLocationsBySubBuilding(
    subBuildingCode: string
  ): Observable<HierarchyItem[]> {
    return this.getDetailCodesByParentCode(subBuildingCode, 'Location');
  }

  // Get Categories (for hierarchy)
  getCategories(): Observable<HierarchyItem[]> {
    return this.getMasterCodeByMultiCode('Category');
  }

  // Get Agents by Job Title
  getAgents(): Observable<any[]> {
    let url: string = `${this.userController}/GetAllUsersByJobTitleId?JobTitle=Agent`;
    return this.getData<any[]>(url);
  }

  // Get Location Types (LocaType)
  getLocationTypes(): Observable<HierarchyItem[]> {
    return this.getMasterCodeByMultiCode('LocaType');
  }

  // Action Plan Master specific methods

  // Get Action Plan Master by ID
  getActionPlanMasterById(actionPlanId: string): Observable<any[]> {
    let url: string = `${this.actionPlanMasterController}/GetActionPlanMasterById?id=${actionPlanId}`;
    return this.getData<any[]>(url);
  }

  // Get All Action Plan Details Summary by Master ID (Legacy)
  getAllActionPlanDetailsSummaryByMasterId(
    actionPlanMasterId: string
  ): Observable<any[]> {
    let url: string = `${this.actionPlanController}/ActionPlanSummeryQuestionWithItems?apMasterId=${actionPlanMasterId}&UserId=0`;
    return this.getData<any[]>(url);
  }

  // Get All Action Plan Details Summary by Master ID (New API)
  getAllActionPlanDetailsSummeryByMasterID(
    actionPlanMasterId: string
  ): Observable<any[]> {
    let url: string = `${this.actionPlanDetailsController}/GetAllActionPlanDetailsSummeryByMasterID?apMasterId=${actionPlanMasterId}`;
    return this.getData<any[]>(url);
  }

  // Get All Action Plan Details by Master ID (Questions)
  getAllActionPlanDetailsByMasterId(
    actionPlanMasterId: string
  ): Observable<any[]> {
    let url: string = `${this.actionPlanController}/ActionPlanDetailsQuestionWithItems?apMasterId=${actionPlanMasterId}&UserId=0`;
    return this.getData<any[]>(url);
  }

  // Get All Action Plan Details by Master ID (Main Details)
  getAllActionPlanDetailsByMasterID(
    actionPlanMasterId: string
  ): Observable<any[]> {
    let url: string = `${this.actionPlanDetailsController}/GetAllActionPlanDetailsByMasterID?apMasterId=${actionPlanMasterId}`;
    return this.getData<any[]>(url);
  }

  // Get Action Plan Master By Report ID (used for loading hierarchy data)
  getActionPlanMasterByReportId(
    reportId: string | null,
    actionPlanMasterId: string
  ): Observable<any[]> {
    let url: string = `${this.actionPlanMasterController}/GetActionPlanMasterByReportId?`;
    if (reportId && reportId !== 'null') {
      url += `ReportId=${reportId}`;
    }
    if (actionPlanMasterId) {
      url += `${reportId && reportId !== 'null' ? '&' : ''}APMasterId=${actionPlanMasterId}`;
    }
    return this.getData<any[]>(url);
  }

  // Get All Department Admins by Action Plan Master ID
  getAllActionPlanDAdminByAPMasterId(
    actionPlanMasterId: string
  ): Observable<any[]> {
    let url: string = `${this.actionPlanDAdminController}/GetAllActionPlanDAdminByAPMasterId?apMasterId=${actionPlanMasterId}`;
    return this.getData<any[]>(url);
  }

  // Get APD Status List
  getAPDStatusList(): Observable<any[]> {
    let url: string = `${this.detailCodeController}/GetMasterCodeByMultiCode?masterCode=${encodeURIComponent('APDetailsItemStatus')}&UserId=&GetPadminAssignedDetails=`;
    return this.getData<any[]>(url);
  }

  // Get All CheckList Action Plan
  getAllCheckListActionPlan(filter: any): Observable<any> {
    let url: string = `${this.actionPlanController}/GetAllCheckListActionPlan`;
    return this.postData<any>(url, filter);
  }

  // Get All Department Admins
  getAllDepartmentAdmins(filter: any): Observable<any> {
    let url: string = `${this.actionPlanController}/GetAllDeparmenAdmins`;
    return this.postData<any>(url, filter);
  }

  // Update Action Plan Detail
  updateActionPlanDetail(data: any): Observable<any> {
    let url: string = `${this.actionPlanDetailsController}/Update`;
    return this.postData<any>(url, data);
  }

  // Update Action Plan Items (Priority, Status, Follow Up, Action Text)
  updateActionPlanItems(data: any): Observable<any> {
    let url: string = `${this.actionPlanItemsAPIController}/UpdateActionPlanItems`;
    return this.postData<any>(url, data);
  }

  // Update Action Plan Item Priority
  updateActionPlanItemPriority(data: any): Observable<any> {
    let url: string = `${this.actionPlanDetailsController}/UpdatePriority`;
    return this.postData<any>(url, data);
  }

  // Update Action Plan Detail List
  updateActionPlanDetailList(detailList: any[]): Observable<any> {
    let url: string = `${this.actionPlanDetailsController}/UpdateAPDetailList`;
    return this.postData<any>(url, detailList);
  }

  // Save Workflow Status
  saveWorkflowStatus(actionPlanId: string, userType: string): Observable<any> {
    let url: string = `${this.mysteryShopperUrl}/TemplatesAPI/SaveWorkflowStatus?reportId=${actionPlanId}&userType=${userType}`;
    return this.getData<any>(url);
  }

  // Action Plan Not Applicable
  actionPlanNotApplicable(actionPlanId: string): Observable<any> {
    let url: string = `${this.actionPlanMasterController}/ActionPlanNotApplicable?APMasterId=${actionPlanId}`;
    return this.getData<any>(url);
  }

  // Update Question Comment
  updateQuestionComment(comment: any, userId: string): Observable<any> {
    let url: string = `${this.actionPlanController}/UpdateQuestionComment?userId=${userId}`;
    return this.postData<any>(url, comment);
  }

  // Update Summary Comment
  updateSummaryComment(summary: any, userId: string): Observable<any> {
    let url: string = `${this.actionPlanController}/UpdateSummeryComment?userId=${userId}`;
    return this.postData<any>(url, summary);
  }

  // Delete Action Plan DAdmin
  deleteActionPlanDAdmin(
    reportId: string,
    actionPlanId: string
  ): Observable<any> {
    // Assuming the API endpoint and method based on AngularJS usage
    let url: string = `${this.actionPlanMasterController}/DeleteActionPlanDAdmin?reportId=${reportId}&actionPlanId=${actionPlanId}`;
    return this.postData<any>(url, {}); // Assuming it's a POST request
  }

  // Assign Dadmins To Action Plan Details
  assignDadminsToActionPlanDetails(
    actionPlanId: string,
    dAdmins: any[]
  ): Observable<any> {
    // Assuming the API endpoint and method based on AngularJS usage
    let url: string = `${this.actionPlanMasterController}/AssignDadminsToActionPlanDetails?actionPlanId=${actionPlanId}`;
    return this.postData<any>(url, dAdmins); // Assuming it's a POST request with the list in the body
  }

  // Feedback related methods

  // Get All FeedBack by Master ID
  getAllFeedBackByMasterId(actionPlanMasterId: string): Observable<any[]> {
    let url: string = `${this.actionPlanDetailsController}/GetAllFeedBackByMasterID?id=${actionPlanMasterId}`;
    return this.getData<any[]>(url);
  }

  // Add FeedBack
  addFeedBack(feedback: any): Observable<any> {
    let url: string = `${this.actionPlanController}/AddFeedBack`;
    return this.postData<any>(url, feedback);
  }

  // Edit FeedBack
  editFeedBack(feedback: any): Observable<any> {
    let url: string = `${this.actionPlanController}/EditFeedBack`;
    return this.postData<any>(url, feedback);
  }

  // Action Plan Item Master specific methods

  // Get All Action Plan Item Summary by Master ID (for ActionPlanItemMaster)
  getAllActionPlanItemSummaryByMasterId(
    actionPlanMasterId: string,
    userId: number
  ): Observable<any> {
    let url: string = `${environment.APIUrl}/Api/ChecklistActionPlan/ActionPlanSummeryQuestionWithItems?apMasterId=${actionPlanMasterId}&UserId=${userId}`;
    return this.getData<any>(url);
  }

  // Get All Action Plan Item Summary by Master ID and Summary Type ID (for ActionPlanItemMaster)
  // Includes retry logic (2 retries with 1 second delay) for handling intermittent 500 errors
  getAllActionPlanItemSummaryByMasterIdAndType(
    actionPlanMasterId: string,
    userId: number,
    summeryTypeId: string
  ): Observable<any> {
    let url: string = `${environment.APIUrl}/Api/ChecklistActionPlan/ActionPlanSummeryQuestionWithItems?apMasterId=${actionPlanMasterId}&UserId=${userId}&SummeryTypeId=${summeryTypeId}`;
    return this.getData<any>(url).pipe(
      retry({
        count: 2,
        delay: (error, retryCount) => {
          return timer(1000); // 1 second delay between retries
        },
      })
    );
  }

  // Get All Action Plan Item Question by Master ID (for ActionPlanItemMaster)
  getAllActionPlanItemQuestionByMasterId(
    actionPlanMasterId: string,
    userId: number
  ): Observable<any> {
    let url: string = `${environment.APIUrl}/Api/ChecklistActionPlan/ActionPlanDetailsQuestionWithItems?apMasterId=${actionPlanMasterId}&UserId=${userId}`;
    return this.getData<any>(url);
  }

  // Get All Action Plan Item Question by Master ID with Hierarchy (now simplified to same API)
  getAllActionPlanItemQuestionByMasterIdWithHierarchy(
    actionPlanMasterId: string | null,
    userId: number,
    checklistTitles: string,
    dateFrom: string,
    dateTo: string,
    categoryId: string,
    getOverDueActions: boolean,
    itemId: string,
    pageNumber: number,
    pageSize: number,
    apStatusId: string | null,
    actionStatus: string[],
    searchText: string
  ): Observable<any> {
    const masterId = actionPlanMasterId || '';
    const queryParams = new URLSearchParams();

    queryParams.append('apMasterId', masterId);
    queryParams.append('UserId', userId.toString());
    if (checklistTitles) queryParams.append('ChecklistTitles', checklistTitles);
    if (dateFrom) queryParams.append('DateFrom', dateFrom);
    if (dateTo) queryParams.append('DateTo', dateTo);
    if (categoryId) queryParams.append('CategoryId', categoryId);
    queryParams.append(
      'getOverDueActions',
      getOverDueActions?.toString() || 'false'
    );
    if (itemId) queryParams.append('ItemId', itemId);
    queryParams.append('pageNumber', pageNumber.toString());
    queryParams.append('pageSize', pageSize.toString());
    if (apStatusId) queryParams.append('apStatusId', apStatusId);
    if (actionStatus && actionStatus.length > 0)
      queryParams.append('actionStatus', actionStatus.join(','));
    if (searchText) queryParams.append('searchText', searchText);

    let url: string = `${environment.APIUrl}/Api/ChecklistActionPlan/ActionPlanDetailsQuestionWithItems?${queryParams.toString()}`;
    return this.getData<any>(url);
  }

  // Get All Action Plan Item Summary by Master ID with Hierarchy (now simplified to same API)
  // Includes retry logic (2 retries with 1 second delay) for handling intermittent 500 errors
  getAllActionPlanItemSummaryByMasterIdWithHierarchy(
    actionPlanMasterId: string | null,
    userId: number,
    checklistTitles: string,
    dateFrom: string,
    dateTo: string,
    categoryId: string,
    getOverDueActions: boolean,
    itemId: string,
    summaryType: string,
    pageNumber: number,
    pageSize: number,
    apStatusId: string | null,
    actionStatus: string[]
  ): Observable<any> {
    const masterId = actionPlanMasterId || '';
    const queryParams = new URLSearchParams();

    queryParams.append('apMasterId', masterId);
    queryParams.append('UserId', userId.toString());
    if (checklistTitles) queryParams.append('ChecklistTitles', checklistTitles);
    if (dateFrom) queryParams.append('DateFrom', dateFrom);
    if (dateTo) queryParams.append('DateTo', dateTo);
    if (categoryId) queryParams.append('CategoryId', categoryId);
    queryParams.append(
      'getOverDueActions',
      getOverDueActions?.toString() || 'false'
    );
    if (itemId) queryParams.append('ItemId', itemId);
    if (summaryType) queryParams.append('SummeryTypeId', summaryType);
    queryParams.append('pageNumber', pageNumber.toString());
    queryParams.append('pageSize', pageSize.toString());
    if (apStatusId) queryParams.append('apStatusId', apStatusId);
    if (actionStatus && actionStatus.length > 0)
      queryParams.append('actionStatus', actionStatus.join(','));

    let url: string = `${environment.APIUrl}/Api/ChecklistActionPlan/ActionPlanSummeryQuestionWithItems?${queryParams.toString()}`;
    return this.getData<any>(url).pipe(
      retry({
        count: 2,
        delay: (error, retryCount) => {
          return timer(1000); // 1 second delay between retries
        },
      })
    );
  }

  // Action Plan General FeedBack With Items - simplified to only accept apMasterId and UserId
  actionPlanGeneralFeedBackWithItems(
    actionPlanMasterId: string,
    userId: number
  ): Observable<any[]>;
  actionPlanGeneralFeedBackWithItems(
    actionPlanMasterId: string | null,
    userId: number,
    dateFrom?: string,
    dateTo?: string,
    categoryId?: string,
    getOnlyActionPlans?: boolean,
    itemId?: string,
    pageNumber?: number,
    pageSize?: number,
    apdStatusId?: string | null,
    actionStatus?: string[],
    searchText?: string
  ): Observable<any[]>;
  actionPlanGeneralFeedBackWithItems(
    actionPlanMasterId: string | null,
    userId: number,
    dateFrom?: string,
    dateTo?: string,
    categoryId?: string,
    getOnlyActionPlans?: boolean,
    itemId?: string,
    pageNumber?: number,
    pageSize?: number,
    apdStatusId?: string | null,
    actionStatus?: string[],
    searchText?: string
  ): Observable<any> {
    const masterId = actionPlanMasterId || '';
    const queryParams = new URLSearchParams();

    queryParams.append('apMasterId', masterId);
    queryParams.append('UserId', userId.toString());
    if (dateFrom) queryParams.append('DateFrom', dateFrom);
    if (dateTo) queryParams.append('DateTo', dateTo);
    if (categoryId) queryParams.append('CategoryId', categoryId);
    queryParams.append(
      'getOnlyActionPlans',
      getOnlyActionPlans?.toString() || 'false'
    );
    if (itemId) queryParams.append('ItemId', itemId);
    if (pageNumber !== undefined)
      queryParams.append('pageNumber', pageNumber.toString());
    if (pageSize !== undefined)
      queryParams.append('pageSize', pageSize.toString());
    if (apdStatusId) queryParams.append('apdStatusId', apdStatusId);
    if (actionStatus && actionStatus.length > 0)
      queryParams.append('actionStatus', actionStatus.join(','));
    if (searchText) queryParams.append('searchText', searchText);

    let url: string = `${environment.APIUrl}/Api/ChecklistActionPlan/ActionPlanGeneralFeedBackWithItems?${queryParams.toString()}`;
    return this.getData<any>(url);
  }

  // Get Action Plan Items Action Status Count - simplified to only accept apMasterId and UserId
  getActionPlanItemsActionStatusCountByHierarchy(
    actionPlanMasterId: string | null,
    userId: number,
    dateFrom: string,
    dateTo: string,
    categoryId?: string,
    getOnlyActionPlans?: boolean,
    itemId?: string,
    pageNumber?: number,
    pageSize?: number,
    apdStatusId?: string | null,
    actionStatus?: string,
    searchText?: string,
    apmStatusId?: string | null
  ): Observable<any> {
    const masterId = actionPlanMasterId || '';
    const queryParams = new URLSearchParams();

    queryParams.append('apMasterId', masterId);
    queryParams.append('UserId', userId.toString());
    if (dateFrom) queryParams.append('DateFrom', dateFrom);
    if (dateTo) queryParams.append('DateTo', dateTo);
    if (categoryId) queryParams.append('CategoryId', categoryId);
    queryParams.append(
      'getOnlyActionPlans',
      getOnlyActionPlans?.toString() || 'false'
    );
    if (itemId) queryParams.append('ItemId', itemId);
    if (pageNumber !== undefined)
      queryParams.append('pageNumber', pageNumber.toString());
    if (pageSize !== undefined)
      queryParams.append('pageSize', pageSize.toString());
    if (apdStatusId) queryParams.append('apdStatusId', apdStatusId);
    if (actionStatus) queryParams.append('actionStatus', actionStatus);
    if (searchText) queryParams.append('searchText', searchText);
    if (apmStatusId) queryParams.append('apmStatusId', apmStatusId);

    let url: string = `${environment.APIUrl}/Api/ChecklistActionPlan/GetActionPlanItemsActionStatusCount?${queryParams.toString()}`;
    return this.getData<any>(url);
  }

  // Get Action Plan Items Status Count for Hierarchy
  getActionPlanItemsStatusCountByHierarchy(params: any): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params.userId) queryParams.append('UserId', params.userId);
    if (params.checklistTitles)
      queryParams.append('ChecklistTitles', params.checklistTitles);
    if (params.dateFrom) queryParams.append('DateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('DateTo', params.dateTo);
    if (params.categoryId) queryParams.append('CategoryId', params.categoryId);
    queryParams.append(
      'getOverDueActions',
      params.getOverDueActions?.toString() || 'false'
    );
    if (params.itemId) queryParams.append('ItemId', params.itemId);

    let url: string = `${this.actionPlanController}/GetActionPlanItemsStatusCount?${queryParams.toString()}`;
    return this.getData<any>(url);
  }

  // Export Action Plan Items to Excel
  exportActionPlanItems(filter: any): Observable<ExportActionPlanResponse> {
    const queryParams = new URLSearchParams();

    // Required parameters
    if (filter.userId !== undefined)
      queryParams.append('UserId', filter.userId.toString());

    // Date range parameters
    if (filter.dateFrom) queryParams.append('DateFrom', filter.dateFrom);
    if (filter.dateTo) queryParams.append('DateTo', filter.dateTo);

    // Filter parameters
    if (filter.categoryId) queryParams.append('CategoryId', filter.categoryId);
    if (filter.itemId) queryParams.append('ItemId', filter.itemId);

    // Status parameters
    if (filter.apdStatusId)
      queryParams.append('APStatusId', filter.apdStatusId);
    if (filter.actionStatus) {
      if (
        Array.isArray(filter.actionStatus) &&
        filter.actionStatus.length > 0
      ) {
        queryParams.append('APActionStatus', filter.actionStatus.join(','));
      } else if (typeof filter.actionStatus === 'string') {
        queryParams.append('APActionStatus', filter.actionStatus);
      }
    }

    // Action Plan specific parameters (only for item details export)
    if (filter.apMasterId) queryParams.append('AID', filter.apMasterId);
    if (filter.checklistTitle)
      queryParams.append('ActiveCheckListTitle', filter.checklistTitle);

    // Fixed parameters
    queryParams.append('getOnlyActionPlans', 'false');
    queryParams.append('ExportAllFlag', (filter.exportAll || false).toString());

    let url: string = `${this.actionPlanMasterController}/ExportActionPlanItems?${queryParams.toString()}`;

    // Use the base service's getData method for JSON response
    return this.getData<ExportActionPlanResponse>(url);
  }

  // Dashboard-specific API methods

  // Get Department Statistics for Dashboard
  getDepartmentStatistics(
    divisionId: string | null,
    assetId: string | null,
    buildingId: string | null,
    subBuildingId: string | null,
    categoryId: string | null,
    locationId: string | null,
    createdOnFrom: string | null,
    createdOnTo: string | null,
    chartType: number
  ): Observable<any> {
    const queryParams = new URLSearchParams();

    if (divisionId) queryParams.append('divisionId', divisionId);
    if (assetId) queryParams.append('assetId', assetId);
    if (buildingId) queryParams.append('buildingId', buildingId);
    if (subBuildingId) queryParams.append('subBuildingId', subBuildingId);
    if (categoryId) queryParams.append('categoryId', categoryId);
    if (locationId) queryParams.append('locationId', locationId);
    if (createdOnFrom) queryParams.append('dateFrom', createdOnFrom);
    if (createdOnTo) queryParams.append('dateTo', createdOnTo);
    queryParams.append('chartInd', chartType.toString());

    let url: string = `${this.actionPlanMasterController}/GetDepartmentStatistics?${queryParams.toString()}`;
    return this.postData<any>(url, {});
  }

  // Get Action Plan Master List View for Dashboard
  getActionPlanMasterListViewForDashboard(
    filter: ActionPlanFilter
  ): Observable<any[]> {
    let url: string = `${this.actionPlanMasterController}/GetActionPlanMasterListView`;
    return this.postData<any[]>(url, filter);
  }

  // Action Plan Creation specific methods

  // Get Schedule Hierarchy Data (to get the hierarchy IDs we need for the other APIs)
  getScheduleHierarchyData(scheduleId: string): Observable<any> {
    // This might be a different API that gets the full hierarchy data for a schedule
    // For now, let's try getting it from the schedule details
    return this.getScheduleById(scheduleId);
  }

  // Get Divisions (for action plan creation hierarchy)
  getDivisions(): Observable<HierarchyItem[]> {
    return this.getMasterCodeByMultiCode('Division');
  }

  // Get Assets by Division using correct master ID
  getAssetsByDivisionWithMasterId(
    parentCode: string,
    userId: string = ''
  ): Observable<HierarchyItem[]> {
    const assetMasterCode = '82313969-92AA-4550-95C5-73FD201C958F';
    return this.getDetailCodesByParentCode(parentCode, assetMasterCode);
  }

  // Get Buildings by Asset using correct master ID
  getBuildingsByAssetWithMasterId(
    parentCode: string,
    userId: string = ''
  ): Observable<HierarchyItem[]> {
    const buildingMasterCode = 'E46AAA71-EB24-43F4-B71F-63ABC472A6C4';
    return this.getDetailCodesByParentCode(parentCode, buildingMasterCode);
  }

  // Get Sub Buildings by Building using correct master ID
  getSubBuildingsByBuildingWithMasterId(
    parentCode: string,
    userId: string = ''
  ): Observable<HierarchyItem[]> {
    const subBuildingMasterCode = 'B6D31581-8E16-429E-8592-2CB92E206281';
    return this.getDetailCodesByParentCode(parentCode, subBuildingMasterCode);
  }

  // Get Locations by Sub Building using correct master ID
  getLocationsBySubBuildingWithMasterId(
    parentCode: string,
    userId: string = ''
  ): Observable<HierarchyItem[]> {
    const locationMasterCode = 'Location';
    return this.getDetailCodesByParentCode(parentCode, locationMasterCode);
  }

  // Get Sub Categories by Category
  getSubCategoriesByCategory(
    categoryCode: string
  ): Observable<HierarchyItem[]> {
    const subCategoryMasterId = 'F3C294DA-882A-47EF-BA5D-4986E71A2103';
    return this.getDetailCodesByParentCode(categoryCode, subCategoryMasterId);
  }

  // Get Sub Category 2 by Sub Category
  getSubCategory2sBySubCategory(
    subCategoryCode: string
  ): Observable<HierarchyItem[]> {
    const subCategory2MasterId = '6CBBAF0C-0E20-47F2-A13B-718EDF7A1222';
    return this.getDetailCodesByParentCode(
      subCategoryCode,
      subCategory2MasterId
    );
  }

  // Get Templates (for action plan creation)
  getTemplates(): Observable<any[]> {
    let url: string = `${this.mysteryShopperUrl}/Template/GetAllTemplates`;
    return this.getData<any[]>(url);
  }

  // Get Checklist Titles by Schedule ID (Step 1 - exactly like original)
  getChecklistTitlesByScheduleId(scheduleId: string): Observable<any[]> {
    let url: string = `${this.mysteryShopperUrl}/Reports/GetChecklistTitlesNew?Id=${scheduleId}`;
    return this.getData<any[]>(url);
  }

  // Get Questions by Checklist Title and Schedule ID (Step 2 - exactly like original - GET request)
  getQuestionsByChecklistTitleAndSchedule(
    hierarchyId: string | null,
    checklistTitle: string,
    dateFrom: string,
    dateTo: string,
    scheduleId: string,
    categoryId: string | null
  ): Observable<any[]> {
    // Build URL exactly like the original with && separators
    let url = `${this.mysteryShopperUrl}/Reports/GetQuestionsByChecklistTitleNew?`;

    if (hierarchyId) {
      url += `Id=${hierarchyId}`;
    }
    if (checklistTitle) {
      // encodeURIComponent so titles containing `&` (e.g. "NAVIGATION, SIGNAGE &
      // MOVEMENT") don't prematurely terminate the Title query param and silently
      // return no items. Other params are GUIDs/dates/ints and need no encoding.
      url += `&&Title=${encodeURIComponent(checklistTitle)}`;
    }
    if (dateFrom) {
      url += `&&dateFrom=${dateFrom} 00:00`;
    }
    if (dateTo) {
      url += `&&dateTo=${dateTo} 00:00`;
    }
    if (scheduleId) {
      url += `&&ScheduleId=${scheduleId}`;
    }
    if (categoryId) {
      url += `&&CategoryId=${categoryId}`;
    }

    return this.getData<any[]>(url);
  }

  // Get Need Improvement and Deterioration by Checklist Title and Schedule ID (Step 3 - exactly like original - GET request)
  getNeedImprovementAndDeteorationByChecklistTitleAndSchedule(
    hierarchyId: string | null,
    checklistTitle: string,
    dateFrom: string,
    dateTo: string,
    scheduleId: string,
    categoryId: string | null
  ): Observable<any[]> {
    // Build URL exactly like the original with && separators
    let url = `${this.mysteryShopperUrl}/Reports/GetNeedImprovementAndDeteorationByChecklistTitle?`;

    if (hierarchyId) {
      url += `Id=${hierarchyId}`;
    }
    if (checklistTitle) {
      // encodeURIComponent so titles containing `&` (e.g. "NAVIGATION, SIGNAGE &
      // MOVEMENT") don't prematurely terminate the Title query param and silently
      // return no items. Other params are GUIDs/dates/ints and need no encoding.
      url += `&&Title=${encodeURIComponent(checklistTitle)}`;
    }
    if (dateFrom) {
      url += `&&dateFrom=${dateFrom} 00:00`;
    }
    if (dateTo) {
      url += `&&dateTo=${dateTo} 00:00`;
    }
    if (scheduleId) {
      url += `&&ScheduleId=${scheduleId}`;
    }
    if (categoryId) {
      url += `&&CategoryId=${categoryId}`;
    }
    url += `&&GetCategoryOverAll=false`;

    return this.getData<any[]>(url);
  }

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Get Schedule By ID
  getScheduleById(scheduleId: string): Observable<any> {
    let url: string = `${this.scheduleController}/GetById?id=${scheduleId}`;
    return this.getData<any>(url);
  }

  // Create Action Plan
  createActionPlan(request: any): Observable<any> {
    let url: string = `${this.actionPlanMasterController}/Create`;
    return this.postData<any>(url, request);
  }

  GetAllActionPlanItems(): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanItemsAPIController}/GetAllActionPlanItems`
    );
  }

  GetAllFeedBackByMasterID(id: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanDetailsController}/GetAllFeedBackByMasterID?id=${id}`
    );
  }

  UpdateActionPlanDetail(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanDetailsController}/Update`,
      obj
    );
  }

  UpdateActionPlanItemPriority(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanDetailsController}/UpdatePriority`,
      obj
    );
  }

  AddFeedBack(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanDetailsController}/AddFeedBack`,
      obj
    );
  }

  EditFeedBack(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanDetailsController}/EditFeedBack`,
      obj
    );
  }

  UpdateQuestionComment(obj: any, id: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanDetailsController}/UpdateQuestionComment?id=${id}`,
      obj
    );
  }

  UpdateSummeryComment(obj: any, id: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanDetailsController}/UpdateSummeryComment?id=${id}`,
      obj
    );
  }

  GetActionPlanItemsById(id: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanItemsAPIController}/GetActionPlanItemsById?id=${id}`
    );
  }

  ActionPlanItemsCreate(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanItemsAPIController}/Create`,
      obj
    );
  }

  CreateActionPlanItems(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanItemsAPIController}/CreateActionPlanItems`,
      obj
    );
  }

  GetActionPlanDetails(Id: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanDetailsController}/GetActionPlanDetails?apMasterId=${Id}`
    );
  }

  GetAllActionPlanDetailsByMasterID(Id: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanDetailsController}/GetAllActionPlanDetailsByMasterID?apMasterId=${Id}`
    );
  }

  GetAllActionPlanDetailsSummeryByMasterID(Id: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanDetailsController}/GetAllActionPlanDetailsSummeryByMasterID?apMasterId=${Id}`
    );
  }

  GetAllActionPlanItemSummeryByMasterID(
    Id: any,
    UserId: any,
    ChecklistName: any,
    DateFrom: any,
    DateTo: any,
    CategoryId: any,
    getOnlyActionPlans: any,
    ItemId: any,
    SummeryTypeId: any,
    PageNumber: any,
    PageSize: any,
    APStatusId: any,
    APActionStatus: any,
    SearchText: any,
    followUpWithAgent: any
  ): Observable<any> {
    let url = `${this.actionPlanController}/ActionPlanSummeryQuestionWithItems?`;
    if (Id != null) url += `apMasterId=${Id}`;
    if (UserId != null) url += `&UserId=${UserId}`;
    if (ChecklistName != null) url += `&ChecklistName=${ChecklistName}`;
    if (DateFrom != null) url += `&DateFrom=${DateFrom}`;
    if (DateTo != null) url += `&DateTo=${DateTo}`;
    if (CategoryId != null) url += `&CategoryId=${CategoryId}`;
    if (getOnlyActionPlans != null)
      url += `&getOnlyActionPlans=${getOnlyActionPlans}`;
    if (ItemId != null) url += `&ItemId=${ItemId}`;
    if (PageNumber != null) url += `&PageNumber=${PageNumber}`;
    if (PageSize != null) url += `&PageSize=${PageSize}`;
    if (APStatusId != null) url += `&APStatusId=${APStatusId}`;
    if (APActionStatus != null) url += `&APActionStatus=${APActionStatus}`;
    if (SummeryTypeId != null) url += `&SummeryTypeId=${SummeryTypeId}`;
    if (followUpWithAgent != null)
      url += `&followUpWithAgent=${followUpWithAgent}`;
    return this.getData<any>(url);
  }

  GetAllActionPlanItemQuestionByMasterID(
    Id: any,
    UserId: any,
    ChecklistName: any,
    DateFrom: any,
    DateTo: any,
    CategoryId: any,
    getOnlyActionPlans: any,
    ItemId: any,
    PageNumber: any,
    PageSize: any,
    APStatusId: any,
    APActionStatus: any,
    SearchText: any,
    followUpWithAgent: any
  ): Observable<any> {
    let url = `${this.actionPlanController}/ActionPlanDetailsQuestionWithItems?`;
    if (Id != null) url += `apMasterId=${Id}`;
    if (UserId != null) url += `&UserId=${UserId}`;
    if (ChecklistName != null) url += `&ChecklistName=${ChecklistName}`;
    if (DateFrom != null) url += `&DateFrom=${DateFrom}`;
    if (DateTo != null) url += `&DateTo=${DateTo}`;
    if (CategoryId != null) url += `&CategoryId=${CategoryId}`;
    if (getOnlyActionPlans != null)
      url += `&getOnlyActionPlans=${getOnlyActionPlans}`;
    if (ItemId != null) url += `&ItemId=${ItemId}`;
    if (PageNumber != null) url += `&PageNumber=${PageNumber}`;
    if (PageSize != null) url += `&PageSize=${PageSize}`;
    if (APStatusId != null) url += `&APStatusId=${APStatusId}`;
    if (APActionStatus != null) url += `&APActionStatus=${APActionStatus}`;
    if (followUpWithAgent != null)
      url += `&followUpWithAgent=${followUpWithAgent}`;
    return this.getData<any>(url);
  }

  ActionPlanGeneralFeedBackWithItems(
    Id: any,
    UserId: any,
    DateFrom: any,
    DateTo: any,
    CategoryId: any,
    getOnlyActionPlans: any,
    ItemId: any,
    PageNumber: any,
    PageSize: any,
    APStatusId: any,
    APActionStatus: any,
    SearchText: any
  ): Observable<any> {
    let url = `${this.actionPlanController}/ActionPlanGeneralFeedBackWithItems?`;
    if (Id != null) url += `apMasterId=${Id}`;
    if (UserId != null) url += `&UserId=${UserId}`;
    if (DateFrom != null) url += `&DateFrom=${DateFrom}`;
    if (DateTo != null) url += `&DateTo=${DateTo}`;
    if (CategoryId != null) url += `&CategoryId=${CategoryId}`;
    if (getOnlyActionPlans != null)
      url += `&getOnlyActionPlans=${getOnlyActionPlans}`;
    if (ItemId != null) url += `&ItemId=${ItemId}`;
    if (PageNumber != null) url += `&PageNumber=${PageNumber}`;
    if (PageSize != null) url += `&PageSize=${PageSize}`;
    if (APStatusId != null) url += `&APStatusId=${APStatusId}`;
    if (APActionStatus != null) url += `&APActionStatus=${APActionStatus}`;
    return this.getData<any>(url);
  }

  GetActionPlanItemsStatusCount(
    Id: any,
    UserId: any,
    DateFrom: any,
    DateTo: any,
    CategoryId: any,
    getOnlyActionPlans: any,
    ItemId: any,
    PageNumber: any,
    PageSize: any,
    APStatusId: any,
    APActionStatus: any,
    SearchText: any
  ): Observable<any> {
    let url = `${this.actionPlanController}/GetActionPlanItemsStatusCount?`;
    if (Id != null) url += `apMasterId=${Id}`;
    if (UserId != null) url += `&UserId=${UserId}`;
    if (DateFrom != null) url += `&DateFrom=${DateFrom}`;
    if (DateTo != null) url += `&DateTo=${DateTo}`;
    if (CategoryId != null) url += `&CategoryId=${CategoryId}`;
    if (getOnlyActionPlans != null)
      url += `&getOnlyActionPlans=${getOnlyActionPlans}`;
    if (ItemId != null) url += `&ItemId=${ItemId}`;
    if (PageNumber != null) url += `&PageNumber=${PageNumber}`;
    if (PageSize != null) url += `&PageSize=${PageSize}`;
    if (APStatusId != null) url += `&APStatusId=${APStatusId}`;
    if (APActionStatus != null) url += `&APActionStatus=${APActionStatus}`;
    return this.getData<any>(url);
  }

  GetActionPlanItemsActionStatusCount(
    Id: any,
    UserId: any,
    DateFrom: any,
    DateTo: any,
    CategoryId: any,
    getOnlyActionPlans: any,
    ItemId: any,
    PageNumber: any,
    PageSize: any,
    APStatusId: any,
    APActionStatus: any,
    SearchText: any,
    APMStatusId: any
  ): Observable<any> {
    let url = `${this.actionPlanController}/GetActionPlanItemsActionStatusCount?`;
    if (Id != null) url += `apMasterId=${Id}`;
    if (UserId != null) url += `&UserId=${UserId}`;
    if (DateFrom != null) url += `&DateFrom=${DateFrom}`;
    if (DateTo != null) url += `&DateTo=${DateTo}`;
    if (CategoryId != null) url += `&CategoryId=${CategoryId}`;
    if (getOnlyActionPlans != null)
      url += `&getOnlyActionPlans=${getOnlyActionPlans}`;
    if (ItemId != null) url += `&ItemId=${ItemId}`;
    if (PageNumber != null) url += `&PageNumber=${PageNumber}`;
    if (PageSize != null) url += `&PageSize=${PageSize}`;
    if (APStatusId != null) url += `&APStatusId=${APStatusId}`;
    if (APActionStatus != null) url += `&APActionStatus=${APActionStatus}`;
    if (APMStatusId != null) url += `&APMStatusId=${APMStatusId}`;
    return this.getData<any>(url);
  }

  CreateMasterActionPlan(obj: any): Observable<any> {
    return this.postData<any>(`${this.actionPlanMasterController}/Create`, obj);
  }

  ActionPlanItemsUpdate(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanItemsAPIController}/Update`,
      obj
    );
  }

  Delete(Id: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanItemsAPIController}/Delete?Id=${Id}`
    );
  }

  DeleteAPD(Id: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanDetailsController}/Delete?Id=${Id}`
    );
  }

  GetActionPlanHitory(Id: any, type: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanItemsAPIController}/GetActionPlanHitory?APId=${Id}&type=${type}`
    );
  }

  UpdateActionPlanItems(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanItemsAPIController}/UpdateActionPlanItems`,
      obj
    );
  }

  GetAllCheckListActionPlan(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanController}/GetAllCheckListActionPlan`,
      obj
    );
  }

  GetAllDeparmenAdmins(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanController}/GetAllDeparmenAdmins`,
      obj
    );
  }

  GetDetailDepartmentAdmins(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanController}/GetDetailDepartmentAdmins`,
      obj
    );
  }

  AssignDadminToActionPlanDetail(DetailId: any, obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanController}/AssignDadminToActionPlanDetail?DetailId=${DetailId}`,
      obj
    );
  }

  AssignDadminToActionPlanFeedback(
    GeneralFeedbackId: any,
    obj: any
  ): Observable<any> {
    return this.postData<any>(
      `${this.actionPlanController}/AssignDadminToActionPlanFeedback?GeneralFeedbackId=${GeneralFeedbackId}`,
      obj
    );
  }

  GetAllQuestionActionPlan(apChecklistId: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanController}/GetAllQuestionActionPlan?apChecklistId=${apChecklistId}`
    );
  }

  CreateActionItemComments(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionItemCommentsAPIController}/Create`,
      obj
    );
  }

  UpdateActionItemComments(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionItemCommentsAPIController}/Update`,
      obj
    );
  }

  DeleteActionItemComments(Id: any): Observable<any> {
    return this.getData<any>(
      `${this.actionItemCommentsAPIController}/Delete?Id=${Id}`
    );
  }

  UpdateIsViewed(itemCommentid: any): Observable<any> {
    return this.getData<any>(
      `${this.actionItemCommentsAPIController}/UpdateIsViewed?itemCommentId=${itemCommentid}`
    );
  }

  GetAllUesrs(): Observable<any> {
    return this.getData<any>(`${this.userController}/GetAllUsers`);
  }

  CreateActionItemMembers(obj: any): Observable<any> {
    return this.postData<any>(
      `${this.actionItemMembersAPIController}/Create`,
      obj
    );
  }

  GetAllActionItemMembersByActionItemId(actionItemId: any): Observable<any> {
    return this.getData<any>(
      `${this.actionItemMembersAPIController}/GetAllActionItemMembersByActionItemId?actionItemId=${actionItemId}`
    );
  }

  DeleteActionItemMembers(actionItemId: any): Observable<any> {
    return this.getData<any>(
      `${this.actionItemMembersAPIController}/Delete?actionItemId=${actionItemId}`
    );
  }

  ExportActionPlanItemsToExcel(ReportId: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanItemsAPIController}/ExportActionPlanItemsToExcel?ReportId=${ReportId}`
    );
  }

  ActionPlanNotApplicable(Id: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanMasterController}/ActionPlanNotApplicable?APMasterId=${Id}`
    );
  }

  SendActionPlanReminder(Id: any): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanMasterController}/SendActionPlanReminder?APMasterId=${Id}`
    );
  }

  ExportActionPlanItems(
    UserIdVal: any,
    DateFrom: any,
    DateTo: any,
    CategoryId: any,
    getOnlyActionPlans: any,
    ItemId: any,
    APStatusId: any,
    APActionStatus: any,
    AID: any,
    activeCheckListTab: any,
    ExportAllFlag: any
  ): Observable<any> {
    let url = `${this.actionPlanMasterController}/ExportActionPlanItems?`;
    if (UserIdVal != null) url += `UserId=${UserIdVal}`;
    if (CategoryId != null) url += `&CategoryId=${CategoryId}`;
    if (DateFrom != null) url += `&DateFrom=${DateFrom}`;
    if (DateTo != null) url += `&DateTo=${DateTo}`;
    if (AID != null) url += `&AID=${AID}`;
    if (getOnlyActionPlans != null)
      url += `&getOnlyActionPlans=${getOnlyActionPlans}`;
    if (ItemId != null) url += `&ItemId=${ItemId}`;
    if (APStatusId != null) url += `&APStatusId=${APStatusId}`;
    if (APActionStatus != null) url += `&APActionStatus=${APActionStatus}`;
    if (activeCheckListTab != null)
      url += `&ActiveCheckListTitle=${encodeURIComponent(activeCheckListTab)}`;
    if (ExportAllFlag != null) url += `&ExportAllFlag=${ExportAllFlag}`;
    return this.getData<any>(url);
  }

  GetStructureDepartments(
    divisionId: any,
    assetId: any,
    categoryId: any
  ): Observable<any> {
    return this.getData<any>(
      `${this.actionPlanMasterController}/GetStructureDepartments?divisionId=${divisionId}` +
        (assetId != null ? `&assetId=${assetId}` : '') +
        (categoryId != null ? `&categoryId=${categoryId}` : '')
    );
  }

  // Upload Action Item Attachment
  uploadActionItemAttachment(
    actionItemId: string,
    file: File
  ): Observable<any> {
    const formData = new FormData();
    formData.append('ActionItemId', actionItemId);
    formData.append('UploadedImage', file);

    let url: string = `${this.actionPlanItemsAPIController}/UploadActionItemAttachment`;
    return this.postDataUpload<any>(url, formData);
  }

  // Delete Action Item Attachment
  deleteActionItemAttachment(attachmentId: string): Observable<number> {
    let url: string = `${this.actionPlanItemsAPIController}/DeleteActionItemAttachment?AttachmentId=${attachmentId}`;
    return this.getData<number>(url);
  }

  getActionPlanMasterIdByScheduleId(scheduleId: string): Observable<string> {
    const url = `${this.scheduleController}/GetActionPlanMasterIdByScheduleId?id=${scheduleId}`;
    return this.getData<string>(url);
  }

  // AI Integration API methods
  private aiIntegrationController: string = `${this.mysteryShopperUrl}/AiIntegration`;

  /**
   * Get AI recommendations by hierarchy
   */
  getAiRecommendationsByHierarchy(
    filter: AiRecommendationFilter
  ): Observable<AiRecommendation[]> {
    const queryParams = new URLSearchParams();

    if (filter.year) queryParams.append('year', filter.year.toString());
    if (filter.month) queryParams.append('month', filter.month.toString());
    if (filter.region) queryParams.append('region', filter.region);
    if (filter.division) queryParams.append('division', filter.division);
    if (filter.asset) queryParams.append('asset', filter.asset);
    if (filter.building) queryParams.append('building', filter.building);
    if (filter.subBuilding)
      queryParams.append('subBuilding', filter.subBuilding);
    if (filter.checklistTitles && filter.checklistTitles.length > 0) {
      filter.checklistTitles.forEach(title => {
        queryParams.append('checklistTitles', title);
      });
    }
    // isMsSource - filter by MS source (true) or external sources (false)
    if (filter.isMsSource !== undefined) {
      queryParams.append('isMsSource', filter.isMsSource.toString());
    }
    // msIntegrationStatus - send enum value
    if (filter.msIntegrationStatus !== undefined) {
      queryParams.append(
        'msIntegrationStatus',
        filter.msIntegrationStatus.toString()
      );
    }

    const url = `${this.aiIntegrationController}/GetByHierarchy?${queryParams.toString()}`;
    return this.getData<AiRecommendation[]>(url);
  }

  /**
   * Check if AI data exists for a hierarchy
   */
  hasAiDataForHierarchy(
    scheduleDate?: Date,
    region?: string,
    division?: string,
    asset?: string,
    building?: string,
    subBuilding?: string
  ): Observable<boolean> {
    const queryParams = new URLSearchParams();

    if (scheduleDate)
      queryParams.append('scheduleDate', scheduleDate.toISOString());
    if (region) queryParams.append('region', region);
    if (division) queryParams.append('division', division);
    if (asset) queryParams.append('asset', asset);
    if (building) queryParams.append('building', building);
    if (subBuilding) queryParams.append('subBuilding', subBuilding);

    const url = `${this.aiIntegrationController}/HasAiData?${queryParams.toString()}`;
    return this.getData<boolean>(url);
  }

  /**
   * Get all AI integration records
   */
  getAllAiRecommendations(): Observable<AiRecommendation[]> {
    const url = `${this.aiIntegrationController}/GetAll`;
    return this.getData<AiRecommendation[]>(url);
  }

  getAllAiRecommendationsPaged(
    filter: AiRecommendationPagedFilter
  ): Observable<AiRecommendationPagedResult> {
    const url = `${this.aiIntegrationController}/GetAllPaged`;
    return this.postData<AiRecommendationPagedResult>(url, filter);
  }

  /**
   * Get AI integration record by ID
   */
  getAiRecommendationById(id: number): Observable<AiRecommendation> {
    const url = `${this.aiIntegrationController}/GetById?id=${id}`;
    return this.getData<AiRecommendation>(url);
  }

  /**
   * Update AI integration status
   */
  updateAiIntegrationStatus(id: number, status: number): Observable<any> {
    const url = `${this.aiIntegrationController}/UpdateStatus?id=${id}&status=${status}`;
    return this.postData<any>(url, {});
  }

  /**
   * Update AI integration record (full update)
   */
  updateAiRecommendation(record: AiRecommendation): Observable<any> {
    const url = `${this.aiIntegrationController}/Update`;
    return this.postData<any>(url, record);
  }

  /**
   * Import AI integration records from Excel file
   * @param file Excel file (.xlsx or .xls)
   * @returns Import result with counts and errors
   */
  importAiRecommendationsFromExcel(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.aiIntegrationController}/ImportFromExcel`;
    return this.postDataUpload<any>(url, formData);
  }

  /**
   * Update AI recommendation fields (Comments, IssuePhrase, Recommendation)
   * Only allowed for records with MsIntegrationStatus = 0 (Pending/Not Processed)
   * Only accessible by Project Admin and Project Head
   * @param data Object containing id, comments, issuePhrase, recommendation
   * @returns Result indicating success or failure with message
   */
  updateAiRecommendationFields(data: {
    id: string;
    comments?: string;
    issuePhrase?: string;
    recommendation?: string;
  }): Observable<{ success: boolean; message: string }> {
    const url = `${this.aiIntegrationController}/UpdateAiRecommendationFields`;
    return this.postData<{ success: boolean; message: string }>(url, data);
  }

  /**
   * Get unmatched AI recommendations (AI items with no corresponding visit)
   * grouped by hierarchy + month + checklist title, with pagination
   */
  getUnmatchedAiRecommendations(filter: any): Observable<any> {
    const url = `${this.aiIntegrationController}/GetUnmatchedAiRecommendations`;
    return this.postData<any>(url, filter);
  }

  /**
   * Get count of unmatched AI recommendation groups
   */
  getUnmatchedAiRecommendationCount(filter: any): Observable<any> {
    const url = `${this.aiIntegrationController}/GetUnmatchedAiRecommendationCount`;
    return this.postData<any>(url, filter);
  }

  skipAiRecommendation(group: any): Observable<any> {
    const url = `${this.aiIntegrationController}/SkipAiRecommendation`;
    return this.postData<any>(url, group);
  }

  unskipAiRecommendation(group: any): Observable<any> {
    const url = `${this.aiIntegrationController}/UnskipAiRecommendation`;
    return this.postData<any>(url, group);
  }

  /**
   * Update AI recommendation fields in CHLVisit_SummeryTB (SummeryValue JSON)
   * Used for action plan master screen editing
   * Only allowed for records with MsIntegrationStatus = 0 (Pending/Not Processed) in the JSON
   * Only accessible by Project Admin and Project Head
   * @param data Object containing summaryId, comments, issuePhrase, recommendation
   * @returns Result indicating success or failure with message
   */
  updateSummaryAiFields(data: {
    summaryId: number;
    comments?: string;
    issuePhrase?: string;
    recommendation?: string;
  }): Observable<{ success: boolean; message: string }> {
    const url = `${this.aiIntegrationController}/UpdateSummaryAiFields`;
    return this.postData<{ success: boolean; message: string }>(url, data);
  }
}
